<template lang="pug">
.family-service-plan-tab(v-cloak)
  .mb-2
  .box-body(style="padding:0;")
    .register-body
      .register-row(v-show="0 < errorMessages.length")
        .register-group.error-message-content
          .register-value(v-for="errorMessage in errorMessages")
            .error-message {{ errorMessage }}

      .register-row(v-if="_useFamilyService")
        .register-group
          .register-key
            span.key-name 現在のプラン
          .register-value(v-if="0 <= currentPlanType && 0 <= currentPlan")
            span.current-plan-name {{ getPlanName(currentPlanType, currentPlan) }}{{ _validPeriodFrom }}
      .register-row(v-else)
        .register-group
          .register-key
            span.key-name 現在のプラン
          .register-value
            span.current-plan-name.error-message 未契約

      template(v-if="_useFamilyService")
        .register-row(v-if="_useFamilyService && createRequestPlanMessage()")
          .register-group
            .register-key
              span.request-plan {{ createRequestPlanMessage() }}
        .register-row(v-if="0 == currentPlan && 2 >= currentPlanType")
          .register-group
            .register-key
              span ライト＋もしくはベーシック＋から他プランへ変更する場合、新規お問い合わせよりご相談ください。

    template(v-if="_useFamilyService")
      PlanTable(ref="planTable" :planTableModel="planTableModel" :isCurrentPlusPlan="isCurrentPlusPlan" :showFreePlan="showFreePlan" @edit="onEditPlan")

      .plan-list-title
        .text
          div 上記表中の金額は税抜金額です。
          div プラン変更は、お客様がプラン変更処理を行った日から適用されます。
          div プラン変更が行われた場合、その翌月より変更後のプランに係る本サービスの料金が適用されます。
        .title アップグレード／ダウングレードの制限
        ul.text
          li アップグレードはいつでも出来ます。登録利用者数制限変更のみのプラン変更もいつでも出来ます。
          li 有料プランから無料プランへのダウングレードは出来ません（無料評価期間を除く）。
          li 有料プラン間のダウングレードは、アップグレード実施日の翌月以降である必要があります（無料評価期間を除く）。
          li 固定料金プランと従量課金制プラン間のプラン変更は、プラン変更実施日の翌月以降である必要があります（無料評価期間を除く）。
          li ダウングレードは、ダウングレード後に使用不可となる設定等を削除後にプラン変更する必要があります。
        .title フリープランのアカウント制限
        ul.text
          li フリープランは同一施設で1アカウントしか作成できません（フリープランと有料プランの組み合わせも出来ません）。

        button.btn.plan-detail-btn(@click="onPlanFunctionDetail") プランの機能比較詳細
        button.btn(@click="onPlanChangeDetail") プラン変更の反映と請求について

  PlanChangeModal(@register="requestPlanChange" :currentPlanType="currentPlanType" :requestPlanName="getPlanName(requestPlanType, requestPlan)" v-model="showUpdateModal")
  OkModal(v-model="showUpdatePlanSuccessModal" title="プランを変更しました。" @ok="createView")
  OkModal(v-model="showUpdatePlanFailureModal" message="プラン変更に必要な条件を満たしていない為、プラン変更できませんでした。\n画面上部に記載の内容をご確認・ご対応いただき、再度実行してください。")
  PlanFunctionDetailModal(ref="planFunctionDetailModal")
  PlanChangeDetailModal(ref="planChangeDetailModal")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import FacilitiesService, { FacilityGetModel } from "@/commons/services/Facilities";
import ResidentsService from "@/commons/services/Residents";
import PlansService, { Plan, PlansGetModel, PlanType } from "@/commons/services/Plans";
import PlanRequestsService, { PlanRequestResponse } from "@/commons/services/PlanRequests";
import SystemError from "@/commons/models/SystemError";

import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import PlanChangeModal from "@/pc/components/modals/PlanChangeModal.vue";
import PlanFunctionDetailModal from "@/pc/components/modals/PlanFunctionDetailModal.vue";
import PlanChangeDetailModal from "@/pc/components/modals/PlanChangeDetailModal.vue";
import PlanTable, { PlanDetail, PlanTableModel } from "@/pc/containers/settings/PlanTable.vue";

/**
 * ### 家族サービスプラン確認・変更画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    PlanChangeModal,
    OkModal,
    PlanFunctionDetailModal,
    PlanChangeDetailModal,
    PlanTable
  }
})
export default class FamilyServicePlanTab extends Vue {
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;
  /** 利用者サービス */
  @Inject("ResidentsService") residentsService!: ResidentsService;
  /** プラン変更依頼サービス */
  @Inject("PlanRequestsService") planRequestsService!: PlanRequestsService;
  /** プランサービス */
  @Inject("PlansService") plansService!: PlansService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** プラン更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** プラン更新完了モーダル表示可否 */
  showUpdatePlanSuccessModal: boolean = false;
  /** プラン更新失敗モーダル表示可否 */
  showUpdatePlanFailureModal: boolean = false;

  currentPlanType: PlanType = PlanType.UNKNOWN;
  currentPlan: Plan = Plan.UNKNOWN;

  /** プラン変更依頼 */
  planRequest: PlanRequestResponse | null = null;
  /** プラン */
  planTableModel: PlanTableModel | null = null;
  /** プラン情報のMAP */
  planMap: Map<string, PlansGetModel> = new Map<string, PlansGetModel>();
  /** 現在プランが＋プランか */
  isCurrentPlusPlan: boolean = false;
  /** フリープランを表示するか */
  showFreePlan: boolean = false;

  /** エラーメッセージ */
  errorMessages: string[] = [];
  /** 更新対象プラン種別、モーダルを経由するので一時保持する */
  requestPlanType: PlanType = PlanType.UNKNOWN;
  /** 更新対象プラン、モーダルを経由するので一時保持する */
  requestPlan: Plan = Plan.UNKNOWN;

  get _useFamilyService(): boolean {
    return userStore.facility.useFamilyService;
  }

  get _validPeriodFrom(): string {
    const now: number = Number(this.$dateUtil.convertDateToNumber(new Date(), "yyyymmddhhMMss") + "000");

    if (userStore.facility.validPeriodFrom && now <= userStore.facility.validPeriodFrom) {
      const year: number = Number(this.$dateUtil.convertNumberToString(userStore.facility.validPeriodFrom, "yyyy"));
      const month: number = Number(this.$dateUtil.convertNumberToString(userStore.facility.validPeriodFrom, "mm"));
      const day: number = Number(this.$dateUtil.convertNumberToString(userStore.facility.validPeriodFrom, "dd"));
      let date: Date = new Date(year, month - 1, day);

      date.setDate(date.getDate() - 1);

      const periodFrom: string = this.$dateUtil.convertDateToString(date, "yyyy年mm月dd日", false);

      return `（${periodFrom}まで有料プランの無料評価期間）`;
    } else {
      return "";
    }
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面表示情報を作成する
   *
   * - 家族連絡サービスを使用しない場合は画面表示処理は無いので即returnする
   */
  public async createView(): Promise<void> {
    if (!this._useFamilyService) {
      return;
    }

    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // エラーメッセージをリセットする
    this.errorMessages = [];

    this.currentPlanType = userStore.facility.planType;
    this.currentPlan = userStore.facility.plan;

    const request: PlanRequestResponse | null = await this.getPlanRequest();
    this.planRequest = request;

    const plans: Array<PlansGetModel> = await this.getPlans();
    const planTableModel: PlanTableModel = this.createPlanTableModel(plans, userStore.facility);
    this.planTableModel = planTableModel;

    if (userStore.facility.validPeriodFrom) {
      const showFreePlan: boolean = this.judgeShowFreePlan(userStore.facility.validPeriodFrom, this.currentPlanType);
      this.showFreePlan = showFreePlan;
    }

    const planMap: Map<string, PlansGetModel> = this.createPlanMap(plans);
    this.planMap = planMap;

    this.isBusy = false;

    return;
  }

  /**
   * ### プラン変更依頼を取得する
   *
   * - プラン変更依頼をしていない場合はnullを返す
   */
  public async getPlanRequest(): Promise<PlanRequestResponse | null> {
    let response: PlanRequestResponse | null = null;
    try {
      response = await this.planRequestsService.get(userStore);
    } catch (error) {
      // プラン変更依頼をしていない場合、ステータスコード404が返ってくる
      // プラン変更依頼をしていなくても問題ないのでその場合はnullを返す
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 404) {
        return response;
      } else {
        // 404以外は共通のエラーハンドラーに処理を委譲する
        throw error;
      }
    }

    return response;
  }

  /**
   * ### プランを取得する
   */
  public async getPlans(): Promise<Array<PlansGetModel>> {
    const response: Array<PlansGetModel> = await this.plansService.gets(userStore);
    return response;
  }

  /**
   * ### プランテーブルの表示に必要な情報を作成する
   *
   * - 並び順やプランの数等、仕様に引きずられる部分があるので仕様に合わせたデータの作り方になる
   */
  public createPlanTableModel(plans: Array<PlansGetModel>, facility: FacilityGetModel): PlanTableModel {
    let freePlan: PlanDetail | undefined = undefined;
    let lightPlans: Array<PlanDetail> = [];
    let lightPlanPlus: PlanDetail | undefined = undefined;
    let basicPlans: Array<PlanDetail> = [];
    let basicPlanPlus: PlanDetail | undefined = undefined;

    for (const plan of plans) {
      if (plan.type == PlanType.FREE) {
        freePlan = {
          ...plan,
          isCurrent: this.isCurrentPlan(plan, facility)
        };
      }

      if (plan.type == PlanType.LIGHT) {
        if (plan.plan == Plan.PLAN_PLUS) {
          const isCurrent: boolean = this.isCurrentPlan(plan, facility);
          if (isCurrent == true) {
            this.isCurrentPlusPlan = true;
          }

          lightPlanPlus = {
            ...plan,
            isCurrent: isCurrent
          };
        } else {
          lightPlans.push({
            ...plan,
            isCurrent: this.isCurrentPlan(plan, facility)
          });
        }
      }

      if (plan.type == PlanType.BASIC) {
        if (plan.plan == Plan.PLAN_PLUS) {
          const isCurrent: boolean = this.isCurrentPlan(plan, facility);
          if (isCurrent == true) {
            this.isCurrentPlusPlan = true;
          }

          basicPlanPlus = {
            ...plan,
            isCurrent: isCurrent
          };
        } else {
          basicPlans.push({
            ...plan,
            isCurrent: this.isCurrentPlan(plan, facility)
          });
        }
      }
    }

    lightPlans.push(lightPlanPlus as PlanDetail);
    basicPlans.push(basicPlanPlus as PlanDetail);

    const model: PlanTableModel = {
      freePlan: freePlan as PlanDetail,
      lightPlans: lightPlans,
      basicPlans: basicPlans
    };

    return model;
  }

  /**
   * ### 現在プランかを返す
   */
  public isCurrentPlan(plan: PlansGetModel, facility: FacilityGetModel): boolean {
    if (facility.planType == plan.type && facility.plan == plan.plan) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * ### フリープランを表示するか否かを返す
   *
   * - 無料期間中であれば表示する
   * - 現在プランがフリープランであれば表示する
   * - それ以外は非表示にする
   */
  public judgeShowFreePlan(validPeriodFrom: number, currentPlanType: PlanType): boolean {
    const now: number = Number(this.$dateUtil.convertDateToNumber(new Date(), "yyyymmddhhMMss") + "000");
    if (now <= validPeriodFrom) {
      return true;
    }

    if (currentPlanType == PlanType.FREE) {
      return true;
    }

    return false;
  }

  /**
   * ### プラン情報のMAPを作成する
   */
  public createPlanMap(plans: Array<PlansGetModel>): Map<string, PlansGetModel> {
    {
      let map: Map<string, PlansGetModel> = new Map<string, PlansGetModel>();

      for (const plan of plans) {
        map.set(`${plan.type}_${plan.plan}`, plan);
      }

      return map;
    }
  }

  /**
   * ### プラン変更ボタン押下イベント
   *
   * - 確認ダイアログを表示する
   */
  public async onEditPlan(plan: PlanDetail): Promise<void> {
    this.showUpdateModal = true;

    // ダイアログを経由してからAPI呼び出しを行うため、変更依頼対象のプラン種別、プランを保持する
    this.requestPlanType = plan.type;
    this.requestPlan = plan.plan;

    return;
  }

  /**
   * ### プラン更新依頼
   *
   * - プラン更新依頼リクエストをAPIに投げる
   */
  public async requestPlanChange(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    try {
      // プラン変更依頼をリクエストする
      // プランは請求処理に関わる情報であり、「月跨ぎ」を考慮する必要がある
      // 画面から日付等の情報を渡すとデータの改ざんが出来てしまうため、リクエストを投げるのみで、
      // planRequetsのレコードを追加・更新・削除するかはAPIが全て管理する
      const requestBody: { userId: string; requestPlanType: PlanType; requestPlan: Plan } = {
        userId: userStore.loginUser.userId,
        requestPlanType: this.requestPlanType,
        requestPlan: this.requestPlan
      };

      await this.planRequestsService.post(userStore, requestBody);

      // エラーが無ければ、最新の施設情報を取得してstoreの内容を更新する
      const facility: FacilityGetModel = await this.getFacility();
      userStore.updateFacility({ facility: facility });

      this.showUpdatePlanSuccessModal = true;
    } catch (error) {
      // プラン変更可能かのチェックでエラーになった場合はステータスコード400なのでその場合はエラーメッセージを表示する
      // エラーメッセージは「,」区切りの文字列なので配列に直してエラーメッセージを作成する
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 400) {
        const originalResponse: { message: string } = JSON.parse(systemError.originalResponse);
        const plan: PlansGetModel = this.getPlan(this.requestPlanType, this.requestPlan) as PlansGetModel;
        this.errorMessages = this.createPlanChangeErrorMessages(originalResponse.message, plan);
        this.showUpdatePlanFailureModal = true;
      } else {
        throw systemError;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 施設を取得する
   */
  public async getFacility(): Promise<FacilityGetModel> {
    const response: FacilityGetModel = await this.facilitiesService.get(userStore);
    return response;
  }

  /**
   * ### プラン変更のバリデーションエラーメッセージを作成する
   */
  public createPlanChangeErrorMessages(errorMessage: string, plan: PlansGetModel): string[] {
    let errorMessages: string[] = [];
    const errorCodes: string[] = errorMessage.split(",");

    for (const errorCode of errorCodes) {
      switch (errorCode) {
        // 無料期間エラー
        case RegisterPlanRequestValidationErrorCode.FREE_PERIOD_ERROR: {
          errorMessages.push(`無料期間を過ぎているため、${plan.name}へプラン変更できません`);
          break;
        }
        // 介護記録が使用可
        case RegisterPlanRequestValidationErrorCode.USE_CAREIMAGE_ERROR: {
          errorMessages.push(`介護記録が使用可能になっているため、${plan.name}へプラン変更できません。家族の使用機能画面から該当機能を使用しないに変更してください。`);
          break;
        }
        // イベントが使用可
        case RegisterPlanRequestValidationErrorCode.USE_EVENT_ERROR: {
          errorMessages.push(`イベント情報が使用可能になっているため、${plan.name}へプラン変更できません。家族の使用機能画面から該当機能を使用しないに変更してください。`);
          break;
        }
        // 写真印刷依頼が使用可
        case RegisterPlanRequestValidationErrorCode.USE_PICTURE_ERROR: {
          errorMessages.push(
            `家族からの写真送付（写真印刷依頼）が使用可能になっているため、${plan.name}へプラン変更できません。家族の使用機能画面から該当機能を使用しないに変更してください。`
          );
          break;
        }
        // 外出外泊届けが使用可
        case RegisterPlanRequestValidationErrorCode.USE_OUTING_ERROR: {
          errorMessages.push(`外出外泊届けが使用可能になっているため、${plan.name}へプラン変更できません。家族の使用機能画面から該当機能を使用しないに変更してください。`);
          break;
        }
        // 一般ユーザーが存在する
        case RegisterPlanRequestValidationErrorCode.EXIST_STANDARD_USER_ERROR: {
          errorMessages.push(`一般ユーザーが登録されているため、${plan.name}へプラン変更できません。一般ユーザー管理から一般ユーザーを削除してください。`);
          break;
        }
        // 利用者グループが存在する
        case RegisterPlanRequestValidationErrorCode.EXIST_RESIDENT_GROUP_ERROR: {
          errorMessages.push(`利用者グループが登録されているため、${plan.name}へプラン変更できません。利用者グループ情報一覧から無所属以外の利用者グループを削除してください。`);
          break;
        }
        // 施設関係者グループが存在する
        case RegisterPlanRequestValidationErrorCode.EXIST_OTHERUSER_GROUP_ERROR: {
          errorMessages.push(`施設関係者グループが登録されているため、${plan.name}へプラン変更できません。施設関係者一覧から施設関係者グループを削除してください。`);
          break;
        }
        // 宛先グループが存在する
        case RegisterPlanRequestValidationErrorCode.EXIST_SENDMESSAGE_GROUP_ERROR: {
          errorMessages.push(`宛先グループが登録されているため、${plan.name}へプラン変更できません。宛先グループ情報一覧から宛先グループを全て削除してください。`);
          break;
        }
        // 宛先グループに施設関係者グループが存在する
        case RegisterPlanRequestValidationErrorCode.OTHERUSER_GROUP_IN_SENDMESSAGE_GROUP_ERROR: {
          errorMessages.push(
            `宛先グループに施設関係者のグループが登録されているため、${plan.name}へプラン変更できません。宛先グループ情報一覧から施設関係者のグループを削除してください。`
          );
          break;
        }
        // 同月内にアップグレードして、ダウングレードしようとしている
        case RegisterPlanRequestValidationErrorCode.DOWNDRAGE_ERROR: {
          errorMessages.push(`今月は既にアップグレードを行っているため、${plan.name}へプラン変更できません。来月以降にダウングレードを行ってください。`);
          break;
        }
        // 利用者数上限を超えている
        case RegisterPlanRequestValidationErrorCode.OVER_RESIDENT_REGISTRATION_LIMIT: {
          errorMessages.push(
            `登録利用者数が${plan.residentLimit + 1}人以上のためプランを${plan.name}に変更できません。登録利用者数を${plan.residentLimit}人以下にしてから行ってください。`
          );
          break;
        }
        // メッセージ下書きが存在する
        case RegisterPlanRequestValidationErrorCode.EXIST_DRAFT_MESSAGE_ERROR: {
          errorMessages.push(`メッセージの下書きが登録されているためプランを${plan.name}に変更できません。メッセージの下書きを全て削除してください。`);
          break;
        }
        case RegisterPlanRequestValidationErrorCode.FACILITY_FAMILY_PERMITTED_FUNCTION_FALSE: {
          errorMessages.push(
            `家族個別設定（家族からの連絡）の登録時設定を使用不可に設定しているためプランを${plan.name}に変更できません。家族個別設定（家族からの連絡）の登録時設定を使用可にしてください。`
          );
          break;
        }
        case RegisterPlanRequestValidationErrorCode.EXIST_UNUSE_FAMILY_PERMITTED_FUNCTIONS_ERROR: {
          errorMessages.push(
            `家族個別設定（家族からの連絡）を使用不可に設定している家族ユーザーがいるためプランを${plan.name}に変更できません。全ての家族ユーザーの家族個別設定（家族からの連絡）を使用可にしてください。`
          );
          break;
        }
        case RegisterPlanRequestValidationErrorCode.EXIST_INVITATION_OTHER_OFFICE: {
          errorMessages.push(`本施設が招待した他事業所連携情報が存在します。招待した他事業所連携情報を削除してください。`);
          break;
        }
        // その他
        default:
          break;
      }
    }

    return errorMessages;
  }

  /**
   * ### プラン機能比較詳細モーダルを表示する
   */
  public onPlanFunctionDetail(): void {
    const modal: any = this.$refs.planFunctionDetailModal;
    if (modal) {
      modal.show();
    }

    return;
  }

  /**
   * ### プラン変更詳細モーダルを表示する
   */
  public onPlanChangeDetail(): void {
    const modal: any = this.$refs.planChangeDetailModal;
    if (modal) {
      modal.show();
    }

    return;
  }

  /**
   * ### プランを取得する
   */
  public getPlan(planType: PlanType, plan: Plan): PlansGetModel | undefined {
    if ((planType == PlanType.LIGHT || planType == PlanType.BASIC) && plan == Plan.PLAN_PLUS) {
      const planDetail: PlanDetail = userStore.facility.planDetail as PlanDetail;
      return {
        ...planDetail,
        type: planType,
        plan: plan
      };
    } else {
      const planResponse: PlansGetModel | undefined = this.planMap.get(`${planType}_${plan}`);
      if (planResponse != undefined) {
        return planResponse;
      } else {
        return undefined;
      }
    }
  }

  /**
   * ### プラン名を返す
   */
  public getPlanName(planType: PlanType, plan: Plan): string {
    const planResponse: PlansGetModel | undefined = this.planMap.get(`${planType}_${plan}`);

    if (planResponse != undefined) {
      return planResponse.name;
    } else {
      return "";
    }
  }

  /**
   * ### プラン変更メッセージを作成する
   */
  public createRequestPlanMessage(): string {
    // プラン変更依頼が無ければメッセージを表示する必要はない
    if (!this.planRequest) {
      return "";
    }

    const currentPlanName: string = this.getPlanName(this.planRequest.currentPlanType, this.planRequest.currentPlan);
    const requestPlanName: string = this.getPlanName(this.planRequest.requestPlanType, this.planRequest.requestPlan);
    const month: string = this.$dateUtil.convertNumberToString(this.planRequest.requestDatetime, "yyyy年mm月", false);

    const now: number = Number(this.$dateUtil.convertDateToNumber(new Date(), "yyyymmddhhMMss") + "000");

    const messages: Array<string> = [];
    messages.push(`${month}に${currentPlanName}プランから${requestPlanName}プランへのプラン変更を行いました。`);
    // 無料期間でない、かつフリープランでない
    if (userStore.facility.validPeriodFrom && now > userStore.facility.validPeriodFrom && this.planRequest.currentPlanType != PlanType.FREE) {
      messages.push(`${month}分のご請求金額は${currentPlanName}プランの料金になります。`);
    }

    const message: string = messages.join("\n");

    return message;
  }
}

export enum RegisterPlanRequestValidationErrorCode {
  UNKNOWN = "-1",
  // 無料期間エラー
  FREE_PERIOD_ERROR = "00",
  // 介護記録が使用可
  USE_CAREIMAGE_ERROR = "10",
  // イベントが使用可
  USE_EVENT_ERROR = "11",
  // 写真印刷依頼が使用可
  USE_PICTURE_ERROR = "12",
  // 外出外泊が使用可
  USE_OUTING_ERROR = "13",
  // 一般ユーザーが存在する
  EXIST_STANDARD_USER_ERROR = "20",
  // 利用者グループが存在する
  EXIST_RESIDENT_GROUP_ERROR = "30",
  // 施設関係者グループが存在する
  EXIST_OTHERUSER_GROUP_ERROR = "40",
  // 宛先グループが存在する
  EXIST_SENDMESSAGE_GROUP_ERROR = "50",
  // 宛先グループに施設関係者グループが存在する
  OTHERUSER_GROUP_IN_SENDMESSAGE_GROUP_ERROR = "51",
  // 同月内にアップグレードして、ダウングレードしようとしている
  DOWNDRAGE_ERROR = "60",
  // 利用者数上限を超えている
  OVER_RESIDENT_REGISTRATION_LIMIT = "70",
  // メッセージ下書きが存在する
  EXIST_DRAFT_MESSAGE_ERROR = "80",
  // 施設のデフォルト家族機能使用可否が使用不可
  FACILITY_FAMILY_PERMITTED_FUNCTION_FALSE = "90",
  // 全家族の中で1人以上家族の機能使用可否に使用不可が存在する
  EXIST_UNUSE_FAMILY_PERMITTED_FUNCTIONS_ERROR = "91",
  // 他事業所連携で自施設が招待したものが存在する
  EXIST_INVITATION_OTHER_OFFICE = "100"
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

.family-service-plan-tab {
  max-width: 1150px;

  .request-plan {
    font-size: 18px;
    white-space: pre-wrap;
    color: $validation-label-font-color;
  }

  .plan-list-title {
    .text {
      font-size: 16px;
      margin-bottom: 12px;
    }
    .title {
      font-size: 18px;
      font-weight: bold;
    }
  }

  .plan-detail-btn {
    margin-right: 10px;
  }
}
</style>
